// FilterContext.js - React Context for global filter state management
// ✅ FIXED: Added infinite loop prevention for API 500 errors
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useReducer,
} from "react";
import apiService from "../services/apiService";
import filterCacheManager from "./FilterCacheManager";

// ==================== CONTEXT CREATION ====================

const FilterContext = createContext();

// ==================== ACTION TYPES ====================

const FILTER_ACTIONS = {
	LOADING_START: "LOADING_START",
	LOADING_END: "LOADING_END",
	FILTERS_LOADED: "FILTERS_LOADED",
	FILTERS_ERROR: "FILTERS_ERROR",
	CACHE_STATUS_UPDATED: "CACHE_STATUS_UPDATED",
	MANUAL_REFRESH_START: "MANUAL_REFRESH_START",
	MANUAL_REFRESH_END: "MANUAL_REFRESH_END",
	AUTH_STATUS_CHANGED: "AUTH_STATUS_CHANGED",
	RESET_FAILURE_FLAG: "RESET_FAILURE_FLAG", // ✅ NEW
};

// ==================== INITIAL STATE ====================

const initialState = {
	// Filter data
	filterValues: null,
	safetyFilters: null,

	// Loading states
	isLoading: false,
	isInitialLoad: true,
	isManualRefresh: false,

	// Error handling
	error: null,
	lastError: null,
	hasPermanentFailure: false, // ✅ NEW: Track if loading permanently failed
	lastFailureTime: null, // ✅ NEW: Track when it failed

	// Cache information
	cacheStatus: {
		hasMemoryCache: false,
		hasStorageCache: false,
		lastUpdated: null,
		isExpired: true,
		source: null,
	},

	// Authentication
	isAuthenticated: false,
	user: null,

	// Metadata
	lastUpdated: null,
	loadAttempts: 0,
	initialized: false,
};

// ==================== REDUCER ====================

function filterReducer(state, action) {
	switch (action.type) {
		case FILTER_ACTIONS.LOADING_START:
			return {
				...state,
				isLoading: true,
				error: null,
			};

		case FILTER_ACTIONS.LOADING_END:
			return {
				...state,
				isLoading: false,
				isInitialLoad: false,
				loadAttempts: state.loadAttempts + 1,
			};

		case FILTER_ACTIONS.FILTERS_LOADED:
			return {
				...state,
				filterValues: action.payload.filterValues,
				safetyFilters: action.payload.safetyFilters,
				error: null,
				hasPermanentFailure: false, // ✅ Reset on success
				lastFailureTime: null, // ✅ Reset on success
				lastUpdated: new Date().toISOString(),
				cacheStatus: {
					...state.cacheStatus,
					...action.payload.cacheStatus,
					source: action.payload.source,
				},
				initialized: true,
			};

		case FILTER_ACTIONS.FILTERS_ERROR:
			return {
				...state,
				error: action.payload.error,
				lastError: action.payload.error,
				filterValues: null,
				safetyFilters: null,
				hasPermanentFailure: action.payload.isPermanent || false, // ✅ NEW
				lastFailureTime: action.payload.isPermanent
					? Date.now()
					: state.lastFailureTime, // ✅ NEW
			};

		case FILTER_ACTIONS.CACHE_STATUS_UPDATED:
			return {
				...state,
				cacheStatus: {
					...state.cacheStatus,
					...action.payload,
				},
			};

		case FILTER_ACTIONS.MANUAL_REFRESH_START:
			return {
				...state,
				isManualRefresh: true,
				error: null,
				hasPermanentFailure: false, // ✅ Reset on manual refresh
				lastFailureTime: null, // ✅ Reset on manual refresh
			};

		case FILTER_ACTIONS.MANUAL_REFRESH_END:
			return {
				...state,
				isManualRefresh: false,
			};

		case FILTER_ACTIONS.AUTH_STATUS_CHANGED:
			return {
				...state,
				isAuthenticated: action.payload.isAuthenticated,
				user: action.payload.user,
			};

		case FILTER_ACTIONS.RESET_FAILURE_FLAG: // ✅ NEW
			return {
				...state,
				hasPermanentFailure: false,
				lastFailureTime: null,
				error: null,
			};

		default:
			return state;
	}
}

// ==================== FILTER PROVIDER COMPONENT ====================

export function FilterProvider({ children }) {
	const [state, dispatch] = useReducer(filterReducer, initialState);

	// ==================== CORE FILTER LOADING ====================

	const loadFilters = useCallback(
		async (forceRefresh = false) => {
			// ✅ FIX: Don't retry if we have a permanent failure (unless forced)
			if (!forceRefresh && state.hasPermanentFailure) {
				console.log(
					"🛑 Skipping load - permanent failure detected. Use refreshFilters() to retry.",
				);
				return {
					success: false,
					error: "Previous loading attempt failed. Please refresh manually.",
					data: null,
				};
			}

			if (state.isLoading && !forceRefresh) {
				console.log("🔄 Filter loading already in progress, skipping...");
				return;
			}

			dispatch({ type: FILTER_ACTIONS.LOADING_START });

			try {
				// Create fetch function for cache manager
				const fetchFunction = async () => {
					return await apiService.getDomainFilterValues();
				};

				// Get filters (from cache or API)
				const result = forceRefresh
					? await filterCacheManager.refreshFilters(fetchFunction)
					: await filterCacheManager.getFilters(fetchFunction);

				if (result.success && result.data) {
					// Process safety filters (reuse existing logic)
					const safetyFilters = mapSafetyFilters(result.data);

					dispatch({
						type: FILTER_ACTIONS.FILTERS_LOADED,
						payload: {
							filterValues: result.data,
							safetyFilters: safetyFilters,
							source: result.source,
							cacheStatus: filterCacheManager.getCacheStatus(),
						},
					});

					console.log(`✅ Filters loaded successfully from ${result.source}`);
					return { success: true, data: result.data };
				} else {
					const errorMessage = result.error || "Failed to load filters";

					// ✅ FIX: Mark as permanent failure if API exhausted all retries
					const isPermanent = result.attemptsExhausted === true;

					dispatch({
						type: FILTER_ACTIONS.FILTERS_ERROR,
						payload: {
							error: errorMessage,
							isPermanent: isPermanent, // ✅ NEW
						},
					});

					if (isPermanent) {
						console.error(
							"❌ PERMANENT FAILURE: All retry attempts exhausted. Use refreshFilters() to try again.",
						);
					} else {
						console.error("❌ Failed to load filters:", errorMessage);
					}

					return { success: false, error: errorMessage };
				}
			} catch (error) {
				const errorMessage =
					error.message || "Unexpected error loading filters";
				dispatch({
					type: FILTER_ACTIONS.FILTERS_ERROR,
					payload: {
						error: errorMessage,
						isPermanent: false, // Unexpected errors are not permanent
					},
				});

				console.error("❌ Error in loadFilters:", error);
				return { success: false, error: errorMessage };
			} finally {
				dispatch({ type: FILTER_ACTIONS.LOADING_END });
			}
		},
		[state.isLoading, state.hasPermanentFailure],
	); // ✅ Added hasPermanentFailure dependency

	// ==================== MANUAL REFRESH ====================

	const refreshFilters = useCallback(async () => {
		console.log("🔄 Manual filter refresh requested...");

		dispatch({ type: FILTER_ACTIONS.MANUAL_REFRESH_START });

		try {
			const result = await loadFilters(true); // Force refresh
			return result;
		} finally {
			dispatch({ type: FILTER_ACTIONS.MANUAL_REFRESH_END });
		}
	}, [loadFilters]);

	// ==================== CACHE MANAGEMENT ====================

	const clearCache = useCallback(() => {
		console.log("🧹 Clearing filter cache...");
		filterCacheManager.clearCache();

		dispatch({
			type: FILTER_ACTIONS.CACHE_STATUS_UPDATED,
			payload: filterCacheManager.getCacheStatus(),
		});
	}, []);

	const getCacheInfo = useCallback(() => {
		return filterCacheManager.getCacheStatus();
	}, []);

	// ✅ NEW: Reset failure flag
	const resetFailureFlag = useCallback(() => {
		console.log("🔓 Resetting failure flag - will allow auto-retry");
		dispatch({ type: FILTER_ACTIONS.RESET_FAILURE_FLAG });
	}, []);

	// ==================== PRELOADING ====================

	const preloadFilters = useCallback(async () => {
		if (!state.initialized && !state.isLoading && !state.hasPermanentFailure) {
			console.log("🚀 Preloading filters...");
			return await loadFilters(false);
		}
	}, [
		state.initialized,
		state.isLoading,
		state.hasPermanentFailure,
		loadFilters,
	]);

	// ==================== SAFETY FILTERS MAPPING ====================

	const mapSafetyFilters = useCallback((responseData) => {
		if (!responseData) return null;

		const domainFilters = responseData.domainFilterValues || responseData;
		if (!domainFilters) return null;

		const {
			contentStyle,
			safetyTier,
			safety_tier_1,
			safety_tier_2,
			safety_tier_3,
			content_style_1,
			content_style_2,
			content_style_3,
		} = domainFilters;

		const safeMainTiers = Array.isArray(safetyTier)
			? safetyTier.filter((t) => t !== null)
			: [];
		const safeStyle1 = Array.isArray(content_style_1)
			? content_style_1.filter((s) => s !== null)
			: [];
		const safeStyle2 = Array.isArray(content_style_2)
			? content_style_2.filter((s) => s !== null)
			: [];
		const safeStyle3 = Array.isArray(content_style_3)
			? content_style_3.filter((s) => s !== null)
			: [];

		const allContentStyles = [
			...safeStyle1,
			...safeStyle2,
			...safeStyle3,
		].filter((style, index, arr) => style && arr.indexOf(style) === index);

		return {
			contentStyle: contentStyle || null,
			safetyTiers: {
				main: safetyTier || [],
				tier1: safety_tier_1 || [],
				tier2: safety_tier_2 || [],
				tier3: safety_tier_3 || [],
			},
			contentStyles: {
				style1: safeStyle1,
				style2: safeStyle2,
				style3: safeStyle3,
			},
			getMainTiers: () => safeMainTiers,
			getAllContentStyles: () => allContentStyles,
			hasMainTiers: () => safeMainTiers.length > 0,
			hasTier: (tierName) => safeMainTiers.includes(tierName),
			hasContentStyle: (styleName) => allContentStyles.includes(styleName),
			isFamilyFriendly: () => safeMainTiers.includes("Family"),
			isTeenAppropriate: () =>
				safeMainTiers.includes("Teen") || safeMainTiers.includes("Family"),
			isMatureContent: () => safeMainTiers.includes("Mature"),
			isRestricted: () => safeMainTiers.includes("Restricted"),
			isUniversal: () => safeMainTiers.includes("Universal"),
			isStandard: () => safeMainTiers.includes("Standard"),
		};
	}, []);

	// ==================== EFFECTS ====================

	// Listen to authentication changes
	useEffect(() => {
		const handleAuthChange = (event, status) => {
			dispatch({
				type: FILTER_ACTIONS.AUTH_STATUS_CHANGED,
				payload: {
					isAuthenticated: status.isAuthenticated,
					user: status.user,
				},
			});

			// ✅ FIX: Only load if authenticated AND no filters AND not loading AND no permanent failure
			if (
				status.isAuthenticated &&
				!state.filterValues &&
				!state.isLoading &&
				!state.hasPermanentFailure
			) {
				// ✅ NEW CHECK
				console.log("👤 User authenticated, loading filters...");
				loadFilters(false);
			}
		};

		const unsubscribe = apiService.onTokenChange(handleAuthChange);
		return unsubscribe;
	}, [
		state.filterValues,
		state.isLoading,
		state.hasPermanentFailure,
		loadFilters,
	]); // ✅ Added dependency

	// Listen to cache changes
	useEffect(() => {
		const handleCacheChange = (event, data) => {
			dispatch({
				type: FILTER_ACTIONS.CACHE_STATUS_UPDATED,
				payload: filterCacheManager.getCacheStatus(),
			});

			console.log(`📊 Cache event: ${event}`);
		};

		const unsubscribe = filterCacheManager.onCacheChange(handleCacheChange);
		return unsubscribe;
	}, []);

	// Initial load check
	useEffect(() => {
		// ✅ FIX: Don't load if there's a permanent failure
		if (
			!state.initialized &&
			!state.isLoading &&
			!state.hasPermanentFailure &&
			apiService.isAuthenticated()
		) {
			console.log("🎬 Initial filter load...");
			loadFilters(false);
		}
	}, [
		state.initialized,
		state.isLoading,
		state.hasPermanentFailure,
		loadFilters,
	]); // ✅ Added dependency

	// ✅ NEW: Auto-reset failure flag after 5 minutes (optional recovery)
	useEffect(() => {
		if (state.hasPermanentFailure && state.lastFailureTime) {
			const FAILURE_RESET_DELAY = 5 * 60 * 1000; // 5 minutes

			const timer = setTimeout(() => {
				console.log("⏰ Auto-resetting failure flag after 5 minutes");
				resetFailureFlag();
			}, FAILURE_RESET_DELAY);

			return () => clearTimeout(timer);
		}
	}, [state.hasPermanentFailure, state.lastFailureTime, resetFailureFlag]);

	// ==================== CONTEXT VALUE ====================

	const contextValue = {
		// State
		...state,

		// Actions
		loadFilters,
		refreshFilters,
		clearCache,
		preloadFilters,
		getCacheInfo,
		resetFailureFlag, // ✅ NEW: Allow manual reset

		// Computed values
		hasFilters: Boolean(state.filterValues),
		hasSafetyFilters: Boolean(state.safetyFilters),
		isReady: state.initialized && !state.isLoading && !state.error,

		// Cache status helpers
		isCached:
			state.cacheStatus.hasMemoryCache || state.cacheStatus.hasStorageCache,
		cacheAge: state.cacheStatus.lastUpdated
			? Math.round(
					(Date.now() - new Date(state.cacheStatus.lastUpdated).getTime()) /
						1000 /
						60,
				)
			: null,
	};

	return (
		<FilterContext.Provider value={contextValue}>
			{children}
		</FilterContext.Provider>
	);
}

// ==================== CUSTOM HOOK ====================

export function useFilters() {
	const context = useContext(FilterContext);

	if (context === undefined) {
		throw new Error("useFilters must be used within a FilterProvider");
	}

	return context;
}

// ==================== CONVENIENCE HOOKS ====================

// Hook for just filter values (backwards compatibility)
export function useFilterValues() {
	const { filterValues, isLoading, error, loadFilters } = useFilters();

	return {
		filterValues,
		isLoading,
		error,
		loadFilters,
	};
}

// Hook for safety filters specifically
export function useSafetyFilters() {
	const { safetyFilters, isLoading, error } = useFilters();

	return {
		safetyFilters,
		isLoading,
		error,
		hasSafetyFilters: Boolean(safetyFilters),
	};
}

// Hook for cache management
export function useFilterCache() {
	const {
		cacheStatus,
		refreshFilters,
		clearCache,
		getCacheInfo,
		isCached,
		cacheAge,
	} = useFilters();

	return {
		cacheStatus,
		refreshFilters,
		clearCache,
		getCacheInfo,
		isCached,
		cacheAge,
	};
}
